import React from "react";
import { useItemIcon } from "@ui/hooks/useItemIcon.js";

/**
 * A05b — `<ItemIcon>`
 *
 * Renders an item's inventory icon when art exists, or a procedural CSS
 * letter-tile (first letter of the item name on a colored backdrop)
 * when {@link useItemIcon} resolves to `null`. Mirrors the entity
 * letter-circle fallback discipline (A00).
 *
 * Used by inventory cells, hand buttons, loot rows, and equipment slots.
 */

export interface ItemIconProps {
  readonly itemId: string;
  readonly name: string;
  /**
   * Square pixel size. Default 24 (inventory list density). Pass `"fill"`
   * to stretch to the parent's box (used by hand-button slots in PartyBar
   * where the button itself sets the size via aspect-ratio).
   */
  readonly size?: number | "fill";
  /**
   * Optional override for the letter-tile background color. Defaults to
   * a hash of the itemId so the same item is the same color everywhere.
   */
  readonly tileColor?: string;
}

const PALETTE = [
  "#3a4a6a",
  "#6a3a4a",
  "#3a6a4a",
  "#6a5a3a",
  "#5a3a6a",
  "#3a6a6a",
  "#6a4a3a",
  "#4a3a6a",
];

function pickColor(itemId: string): string {
  let hash = 0;
  for (let i = 0; i < itemId.length; i++) {
    hash = (hash * 31 + itemId.charCodeAt(i)) | 0;
  }
  const idx = Math.abs(hash) % PALETTE.length;
  return PALETTE[idx]!;
}

export function ItemIcon({ itemId, name, size = 24, tileColor }: ItemIconProps): React.ReactElement {
  const { url, loaded } = useItemIcon(itemId);
  const fill = size === "fill";
  const px = fill ? 0 : (size as number);

  const baseStyle: React.CSSProperties = fill
    ? {
        width: "100%",
        height: "100%",
        flex: 1,
        boxSizing: "border-box",
        borderRadius: 2,
      }
    : {
        width: px,
        height: px,
        flexShrink: 0,
        display: "inline-block",
        boxSizing: "border-box",
        borderRadius: 2,
        border: "1px solid rgba(255,255,255,0.08)",
      };

  if (loaded && url !== null) {
    return (
      <img
        src={url}
        alt={name}
        title={name}
        style={{
          ...baseStyle,
          objectFit: "contain",
          background: "rgba(0,0,0,0.25)",
        }}
        data-testid={`item-icon-${itemId}`}
      />
    );
  }

  const letter = name.length > 0 ? name[0]!.toUpperCase() : "?";
  const fontPx = fill ? 18 : Math.round(px * 0.55);
  return (
    <span
      role="img"
      aria-label={name}
      title={name}
      style={{
        ...baseStyle,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: tileColor ?? pickColor(itemId),
        color: "rgba(255,255,255,0.85)",
        fontFamily: "monospace",
        fontSize: fontPx,
        fontWeight: 600,
        userSelect: "none",
      }}
      data-testid={`item-tile-${itemId}`}
    >
      {letter}
    </span>
  );
}
